import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  type ChartOptions,
  type ChartData
} from 'chart.js';
import { Plugin } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { format } from 'date-fns';
import {
  GenderRangeData,
  HistoricalBiomarkerDataElement
} from '../../../types/apiGeneratedTypes';
import { getBiomarkersResultsByAppointmentHistoricalData } from '../../../api/biomarkers';
import Loader from '../../BaseComponents/Loader';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

interface BiomarkerHistoryChartProps {
  rangeData?: GenderRangeData | null;
  biomarker_id: number;
  member_id: string;
  onDataLoaded?: (data: HistoricalBiomarkerDataElement[]) => void;
  unit?: string;
  refreshKey?: number;
}

const getUnitsYLabels = (values: number[]) => {
  if (values.length === 0) {
    return [
      { value: 0, label: '0' },
      { value: 10, label: '10' }
    ];
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const yMin = min;
  const yMax = max === min ? min + 1 : max;
  const tickCount = 5;

  return Array.from({ length: tickCount }, (_, index) => {
    const value = yMin + (yMax - yMin) * (index / (tickCount - 1));
    const label = Number.isInteger(value) ? value.toString() : value.toFixed(1);
    return { value, label };
  });
};

const BiomarkerHistoryChart: React.FC<BiomarkerHistoryChartProps> = ({
  rangeData = null,
  biomarker_id,
  member_id,
  onDataLoaded,
  unit,
  refreshKey = 0
}) => {
  const isUnitsMode = !rangeData;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [chartData, setChartData] = useState<HistoricalBiomarkerDataElement[]>(
    []
  );
  const { selectedTestResultMemberId } = useSelector(
    (state: RootState) => state.alerts
  );

  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      try {
        const resp = await getBiomarkersResultsByAppointmentHistoricalData(
          biomarker_id,
          member_id
        );
        setChartData(resp.historical_data);
        if (onDataLoaded) {
          onDataLoaded(resp.historical_data);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    };
    fetchChartData();
  }, [biomarker_id, selectedTestResultMemberId, member_id, refreshKey, onDataLoaded]);

  const numericValues = useMemo(
    () =>
      chartData
        .map((item) => Number(item.value))
        .filter((value) => Number.isFinite(value)),
    [chartData]
  );

  const getRegionColor = useCallback((value: number): string => {
    if (!rangeData) {
      return '#808080';
    }

    if (
      typeof rangeData.critical_low_from === 'number' &&
      typeof rangeData.critical_low_to === 'number' &&
      value >= rangeData.critical_low_from &&
      value <= rangeData.critical_low_to
    ) {
      return '#d56256';
    }
    if (typeof rangeData.critical_low_to === 'number' && value < rangeData.critical_low_to) {
      return '#d56256';
    }

    if (
      typeof rangeData.low_from === 'number' &&
      typeof rangeData.low_to === 'number' &&
      value >= rangeData.low_from &&
      value <= rangeData.low_to
    ) {
      return '#f97639';
    }
    if (typeof rangeData.low_from === 'number' && value < rangeData.low_from) {
      return '#f97639';
    }

    if (typeof rangeData.high_from === 'number' && value >= rangeData.high_from) {
      return '#f97639';
    }

    if (
      typeof rangeData.optimal_from === 'number' &&
      typeof rangeData.optimal_to === 'number' &&
      value >= rangeData.optimal_from &&
      value <= rangeData.optimal_to
    ) {
      return '#618a3a';
    }

    if (
      typeof rangeData.optimal_to === 'number' &&
      value > rangeData.optimal_to
    ) {
      const highFrom = typeof rangeData.high_from === 'number' ? rangeData.high_from : Infinity;
      if (value < highFrom) {
        return '#a3b43e';
      }
    }

    if (typeof rangeData.below_optimal_from === 'number' && value >= rangeData.below_optimal_from) {
      const optimalFrom = typeof rangeData.optimal_from === 'number' ? rangeData.optimal_from : Infinity;
      if (value < optimalFrom) {
        return '#a3b43e';
      }
    }

    if (
      typeof rangeData.above_optimal_to === 'number' &&
      value > rangeData.above_optimal_to &&
      (typeof rangeData.high_from !== 'number' || rangeData.high_from === null)
    ) {
      return '#a3b43e';
    }

    return '#808080';
  }, [rangeData]);

  const yLabels = useMemo(() => {
    if (isUnitsMode) {
      return getUnitsYLabels(numericValues);
    }

    const getLabel = (value: number | null | undefined): string => {
      if (value === null || value === undefined) return '';
      // Don't filter out 0 - it's a valid value for ranges starting from 0
      return value.toString();
    };

    const rawLabels = [
      {
        value: rangeData.critical_low_from,
        label: getLabel(rangeData.critical_low_from)
      },
      {
        value: rangeData.critical_low_to,
        label: getLabel(rangeData.critical_low_to)
      },
      { 
        value: rangeData.low_from, 
        label: getLabel(rangeData.low_from)
      },
      {
        value: rangeData.low_to,
        label: getLabel(rangeData.low_to)
      },
      { 
        value: rangeData.below_optimal_from, 
        label: getLabel(rangeData.below_optimal_from)
      },
      {
        value: rangeData.below_optimal_to,
        label: getLabel(rangeData.below_optimal_to)
      },
      { 
        value: rangeData.optimal_from, 
        label: getLabel(rangeData.optimal_from)
      },
      {
        value: rangeData.optimal_to,
        label: getLabel(rangeData.optimal_to)
      },
      { 
        value: rangeData.above_optimal_from, 
        label: getLabel(rangeData.above_optimal_from)
      },
      {
        value: rangeData.above_optimal_to,
        label: getLabel(rangeData.above_optimal_to)
      },
      { 
        value: rangeData.high_from, 
        label: getLabel(rangeData.high_from)
      },
      {
        value: rangeData.high_to,
        label: getLabel(rangeData.high_to)
      }
    ];

    if (typeof rangeData.low_from === 'number' && typeof rangeData.low_to === 'number') {
      if (rangeData.low_from !== 0 || rangeData.low_to !== 0) {
        const midpoint = (rangeData.low_from + rangeData.low_to) / 2;
        rawLabels.push({ value: midpoint, label: 'Low' });
      }
    }

    if (typeof rangeData.below_optimal_from === 'number' && typeof rangeData.below_optimal_to === 'number') {
      if (rangeData.below_optimal_from !== 0 || rangeData.below_optimal_to !== 0) {
        const midpoint = (rangeData.below_optimal_from + rangeData.below_optimal_to) / 2;
        rawLabels.push({ value: midpoint, label: 'Below optimal' });
      }
    }

    if (typeof rangeData.optimal_from === 'number' && typeof rangeData.optimal_to === 'number') {
      if (rangeData.optimal_from !== 0 || rangeData.optimal_to !== 0) {
        const midpoint = (rangeData.optimal_from + rangeData.optimal_to) / 2;
        rawLabels.push({ value: midpoint, label: 'Optimal' });
      }
    }

    if (typeof rangeData.above_optimal_from === 'number' && typeof rangeData.above_optimal_to === 'number') {
      if (rangeData.above_optimal_from !== 0 || rangeData.above_optimal_to !== 0) {
        const midpoint = (rangeData.above_optimal_from + rangeData.above_optimal_to) / 2;
        rawLabels.push({ value: midpoint, label: 'Above optimal' });
      }
    }

    if (typeof rangeData.high_from === 'number' && typeof rangeData.high_to === 'number') {
      if (rangeData.high_from !== 0 || rangeData.high_to !== 0) {
        const midpoint = (rangeData.high_from + rangeData.high_to) / 2;
        rawLabels.push({ value: midpoint, label: 'High' });
      }
    }

    // Check if range starts from 0 and add it if not already present
    const hasZero = rawLabels.some(item => typeof item.value === 'number' && item.value === 0);
    if (!hasZero) {
      // Check if any range boundary is 0
      const rangeStartsAtZero = 
        rangeData.critical_low_from === 0 ||
        rangeData.critical_low_to === 0 ||
        rangeData.low_from === 0 ||
        rangeData.low_to === 0;
      
      if (rangeStartsAtZero) {
        rawLabels.push({ value: 0, label: '0' });
      }
    }

    const filteredLabels = rawLabels
      .filter((item) => typeof item.value === 'number' && !isNaN(item.value))
      .sort((a, b) => {
        const aVal = typeof a.value === 'number' ? a.value : 0;
        const bVal = typeof b.value === 'number' ? b.value : 0;
        return aVal - bVal;
      });

    return filteredLabels;
  }, [rangeData, isUnitsMode, numericValues]);

  const yAxisTicksConfig = useMemo(() => {
    if (isUnitsMode) {
      const labelsWithText = yLabels.filter(
        (item) => typeof item.value === 'number' && item.label.trim() !== ''
      );
      const tickValues = labelsWithText.map((item) => item.value as number);
      const tickToLabelMap = new Map(
        labelsWithText.map((item) => [item.value as number, item.label])
      );

      return {
        evenlySpacedTicks: tickValues,
        tickToLabelMap,
        originalValueToTickMap: new Map(tickValues.map((value) => [value, value])),
        mapValueToTick: (value: number | null) => value,
        min: tickValues[0] ?? 0,
        max: tickValues[tickValues.length - 1] ?? 10
      };
    }

    const filteredLabels = yLabels
      .filter(
        (item) => typeof item.value === 'number' && item.label && item.label.trim() !== ''
      )
      .sort((a, b) => {
        const aVal = typeof a.value === 'number' ? a.value : 0;
        const bVal = typeof b.value === 'number' ? b.value : 0;
        return aVal - bVal;
      });
    
    const seenLabels = new Set<string>();
    const labelsWithText = filteredLabels.filter((item) => {
      const labelText = item.label.trim();
      if (seenLabels.has(labelText)) {
        return false;
      }
      seenLabels.add(labelText);
      return true;
    });
    
    if (labelsWithText.length === 0) {
      const min = yLabels[0]?.value as number ?? 0;
      const max = yLabels[yLabels.length - 1]?.value as number ?? 0;
      return {
        evenlySpacedTicks: [],
        tickToLabelMap: new Map<number, string>(),
        originalValueToTickMap: new Map<number, number>(),
        mapValueToTick: (value: number | null) => value,
        min,
        max
      };
    }

    const labelValues = labelsWithText
      .map(item => typeof item.value === 'number' ? item.value : null)
      .filter((v): v is number => v !== null);
    
    if (labelValues.length === 0) {
      const min = yLabels[0]?.value as number ?? 0;
      const max = yLabels[yLabels.length - 1]?.value as number ?? 0;
      return {
        evenlySpacedTicks: [],
        tickToLabelMap: new Map<number, string>(),
        originalValueToTickMap: new Map<number, number>(),
        mapValueToTick: (value: number | null) => value,
        min,
        max
      };
    }

    const min = Math.min(...labelValues);
    const max = Math.max(...labelValues);
    
    const evenlySpacedTicks: number[] = [];
    const tickCount = labelsWithText.length;
    
    if (tickCount === 1) {
      evenlySpacedTicks.push(min);
    } else {
      for (let i = 0; i < tickCount; i++) {
        const tickValue = min + (max - min) * (i / (tickCount - 1));
        evenlySpacedTicks.push(tickValue);
      }
    }

    const tickToLabelMap = new Map<number, string>();
    evenlySpacedTicks.forEach((tickValue, index) => {
      if (index < labelsWithText.length) {
        tickToLabelMap.set(tickValue, labelsWithText[index].label);
      }
    });

    const originalValueToTickMap = new Map<number, number>();
    labelsWithText.forEach((labelItem, index) => {
      if (typeof labelItem.value === 'number' && index < evenlySpacedTicks.length) {
        originalValueToTickMap.set(labelItem.value, evenlySpacedTicks[index]);
      }
    });

    const mapValueToTick = (originalValue: number | null): number | null => {
      if (originalValue === null) return null;
      
      if (originalValueToTickMap.has(originalValue)) {
        return originalValueToTickMap.get(originalValue)!;
      }
      
      const sortedLabels = [...labelsWithText]
        .filter(item => typeof item.value === 'number')
        .sort((a, b) => {
          const aVal = typeof a.value === 'number' ? a.value : 0;
          const bVal = typeof b.value === 'number' ? b.value : 0;
          return aVal - bVal;
        });
      
      if (sortedLabels.length === 0) return originalValue;
      
      const sortedValues = sortedLabels.map(item => typeof item.value === 'number' ? item.value : 0);
      
      if (originalValue <= sortedValues[0]) {
        return evenlySpacedTicks[0];
      }
      if (originalValue >= sortedValues[sortedValues.length - 1]) {
        return evenlySpacedTicks[evenlySpacedTicks.length - 1];
      }
      
      for (let i = 0; i < sortedValues.length - 1; i++) {
        if (originalValue >= sortedValues[i] && originalValue <= sortedValues[i + 1]) {
          const ratio = (originalValue - sortedValues[i]) / (sortedValues[i + 1] - sortedValues[i]);
          const tick1 = evenlySpacedTicks[i];
          const tick2 = evenlySpacedTicks[i + 1];
          return tick1 + (tick2 - tick1) * ratio;
        }
      }
      
      return originalValue;
    };

    return {
      evenlySpacedTicks,
      tickToLabelMap,
      originalValueToTickMap,
      mapValueToTick,
      min,
      max
    };
  }, [yLabels, isUnitsMode]);

  const data = useMemo<
    ChartData<'line', { x: string; y: number | null; originalValue?: number }[], string>
  >(() => {
    const sortedData = [...chartData].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });

    const mapValueToTick = yAxisTicksConfig.mapValueToTick;

    const dataPoints = sortedData.map((item) => {
      const originalValue = Number(item.value);
      
      if (!Number.isFinite(originalValue)) {
        return {
          x: item.date, 
          y: null
        };
      }

      const mappedValue = mapValueToTick ? mapValueToTick(originalValue) : originalValue;
      
      return {
        x: item.date, 
        y: mappedValue !== null ? mappedValue : originalValue,
        originalValue: originalValue
      };
    });

    return {
      datasets: [
        {
          label: 'Biomarker Value',
          data: dataPoints,
          borderColor: '#0C20121A',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0,
          pointRadius: 4,
          pointBorderWidth: 3,
          pointBackgroundColor: (ctx: any) => {
            const dataPoint = ctx.raw as { originalValue?: number };
            const originalValue = dataPoint?.originalValue ?? ctx.parsed.y;
            
            if (originalValue === null || originalValue === undefined || !Number.isFinite(originalValue)) {
              return '#808080';
            }
            return getRegionColor(originalValue);
          },
          pointBorderColor: (ctx: any) => {
            const dataPoint = ctx.raw as { originalValue?: number };
            const originalValue = dataPoint?.originalValue ?? ctx.parsed.y;
            
            if (originalValue === null || originalValue === undefined || !Number.isFinite(originalValue)) {
              return '#808080';
            }
            return getRegionColor(originalValue);
          }
        }
      ]
    };
  }, [chartData, yAxisTicksConfig, getRegionColor]);

  const rangeHighlightPlugin = useMemo<Plugin<'line'>>(() => {
    if (!rangeData || !chartData.length) {
      return {
        id: 'rangeHighlight',
        beforeDraw: () => { }
      };
    }

    const sortedData = [...chartData].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });
    const latestItem = sortedData[sortedData.length - 1];
    const latestValue = Number(latestItem.value);

    if (!Number.isFinite(latestValue)) {
      return {
        id: 'rangeHighlight',
        beforeDraw: () => { }
      };
    }

    const mapValueToTick = yAxisTicksConfig.mapValueToTick;
    const parsed = latestValue;

    const valueColor = getRegionColor(parsed);
    const colorWithAlpha = valueColor + '26'; // Add transparency (26 = ~15% opacity)
    
    let rangeFrom: number | null = null;
    let rangeTo: number | null = null;

    if (
      typeof rangeData.low_from === 'number' &&
      typeof rangeData.low_to === 'number' &&
      parsed >= rangeData.low_from &&
      parsed <= rangeData.low_to
    ) {
      rangeFrom = rangeData.low_from;
      rangeTo = rangeData.low_to;
    }
    else if (
      typeof rangeData.below_optimal_from === 'number' &&
      typeof rangeData.below_optimal_to === 'number' &&
      parsed >= rangeData.below_optimal_from &&
      parsed <= rangeData.below_optimal_to
    ) {
      rangeFrom = rangeData.below_optimal_from;
      rangeTo = rangeData.below_optimal_to;
    }
    else if (
      typeof rangeData.optimal_from === 'number' &&
      typeof rangeData.optimal_to === 'number' &&
      parsed >= rangeData.optimal_from &&
      parsed <= rangeData.optimal_to
    ) {
      rangeFrom = rangeData.optimal_from;
      rangeTo = rangeData.optimal_to;
    }
    else if (
      typeof rangeData.above_optimal_from === 'number' &&
      typeof rangeData.above_optimal_to === 'number' &&
      parsed >= rangeData.above_optimal_from &&
      parsed <= rangeData.above_optimal_to
    ) {
      rangeFrom = rangeData.above_optimal_from;
      rangeTo = rangeData.above_optimal_to;
    }
    else if (
      typeof rangeData.high_from === 'number' &&
      typeof rangeData.high_to === 'number' &&
      parsed >= rangeData.high_from &&
      parsed <= rangeData.high_to
    ) {
      rangeFrom = rangeData.high_from;
      rangeTo = rangeData.high_to;
    }
    else if (typeof rangeData.critical_low_from === 'number' && parsed < rangeData.critical_low_from) {
      rangeFrom = typeof rangeData.critical_low_from === 'number' ? rangeData.critical_low_from : null;
      rangeTo = typeof rangeData.low_from === 'number' ? rangeData.low_from : null;
    }
    else if (typeof rangeData.low_from === 'number' && parsed < rangeData.low_from) {
      rangeFrom = typeof rangeData.critical_low_from === 'number' ? rangeData.critical_low_from : null;
      rangeTo = typeof rangeData.low_from === 'number' ? rangeData.low_from : null;
    }
    else if (typeof rangeData.high_to === 'number' && parsed > rangeData.high_to) {
      rangeFrom = (typeof rangeData.high_to === 'number' && rangeData.high_to !== 0) 
        ? rangeData.high_to 
        : (typeof rangeData.high_from === 'number' ? rangeData.high_from : null);
      rangeTo = null;
    }

    const finalRangeFrom = rangeFrom;
    const finalRangeTo = rangeTo;
    const finalColor = colorWithAlpha;

    return {
      id: 'rangeHighlight',
      beforeDraw: (chart) => {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea) return;

        const yScale = scales.y;

        if (finalRangeFrom === null || finalRangeTo === null) {
          return;
        }

        let rangeFrom = finalRangeFrom;
        let rangeTo = finalRangeTo;

        if (mapValueToTick) {
          const mappedFrom = mapValueToTick(rangeFrom);
          rangeFrom = mappedFrom !== null ? mappedFrom : rangeFrom;
          const mappedTo = mapValueToTick(rangeTo);
          rangeTo = mappedTo !== null ? mappedTo : rangeTo;
        }

        const yFrom = yScale.getPixelForValue(rangeFrom);
        const yTo = yScale.getPixelForValue(rangeTo);

        ctx.save();
        ctx.fillStyle = finalColor;
        ctx.fillRect(chartArea.left, Math.min(yFrom, yTo), chartArea.right - chartArea.left, Math.abs(yTo - yFrom));
        ctx.restore();
      }
    };
  }, [chartData, rangeData, yAxisTicksConfig, getRegionColor]);

  const regionSeparatorPlugin: Plugin<'line'> = useMemo(() => ({
    id: 'regionSeparator',
    beforeDraw: (chart) => {
      const { ctx, chartArea, scales } = chart;
      if (!chartArea) return;

      const yScale = scales.y;
      const tickPositions = yAxisTicksConfig.evenlySpacedTicks;

      ctx.save();
      ctx.strokeStyle = '#b2b2b2';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);

      tickPositions.forEach((tickValue) => {
        const y = yScale.getPixelForValue(tickValue);
        ctx.beginPath();
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.stroke();
      });

      ctx.restore();
    }
  }), [yAxisTicksConfig]);

  const yAxisLabelColorPlugin: Plugin<'line'> = useMemo(() => {
    if (!rangeData || !chartData.length) {
      return {
        id: 'yAxisLabelColor',
        afterDraw: () => { }
      };
    }

    const sortedData = [...chartData].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });
    const latestItem = sortedData[sortedData.length - 1];
    const latestValue = Number(latestItem.value);

    if (!Number.isFinite(latestValue)) {
      return {
        id: 'yAxisLabelColor',
        afterDraw: () => { }
      };
    }

    const getLabelColor = (labelText: string): string => {
      const normalizedLabel = labelText.trim().toLowerCase();
      const valueColor = getRegionColor(latestValue);
      
      const isInLow = (
        valueColor === '#d56256' ||
        (valueColor === '#d56256' && (
          (typeof rangeData.low_from === 'number' && latestValue >= rangeData.low_from && latestValue <= (rangeData.low_to || Infinity)) ||
          (typeof rangeData.low_from === 'number' && latestValue < rangeData.low_from) ||
          (typeof rangeData.critical_low_to === 'number' && latestValue < rangeData.critical_low_to)
        ) && !(
          typeof rangeData.high_from === 'number' && 
          latestValue >= rangeData.high_from && 
          latestValue <= (rangeData.high_to || Infinity)
        ))
      );
      
      const isInBelowOptimal = (
        valueColor === '#a3b43e' &&
        typeof rangeData.below_optimal_from === 'number' && 
        latestValue >= rangeData.below_optimal_from && 
        latestValue <= (rangeData.below_optimal_to || Infinity)
      );
      
      const isInOptimal = (
        valueColor === '#618a3a' &&
        typeof rangeData.optimal_from === 'number' && 
        latestValue >= rangeData.optimal_from && 
        latestValue <= (rangeData.optimal_to || Infinity)
      );
      
      const isInAboveOptimal = (
        valueColor === '#a3b43e' &&
        typeof rangeData.above_optimal_from === 'number' && 
        latestValue >= rangeData.above_optimal_from && 
        latestValue <= (rangeData.above_optimal_to || Infinity)
      );
      
      const isInHigh = (
        valueColor === '#d56256' &&
        typeof rangeData.high_from === 'number' && 
        latestValue >= rangeData.high_from && 
        latestValue <= (rangeData.high_to || Infinity)
      );
      
      if ((normalizedLabel === 'low' || normalizedLabel.includes('low')) && isInLow) {
        return valueColor;
      }
      if ((normalizedLabel === 'below optimal' || normalizedLabel.includes('below optimal') || normalizedLabel.includes('belowoptimal')) && isInBelowOptimal) {
        return valueColor;
      }
      if ((normalizedLabel === 'optimal' || normalizedLabel.includes('optimal')) && isInOptimal) {
        return valueColor;
      }
      if ((normalizedLabel === 'above optimal' || normalizedLabel.includes('above optimal') || normalizedLabel.includes('aboveoptimal')) && isInAboveOptimal) {
        return valueColor;
      }
      if ((normalizedLabel === 'high' || normalizedLabel.includes('high')) && isInHigh) {
        return valueColor;
      }
      
      return 'black';
    };

    return {
      id: 'yAxisLabelColor',
      afterDraw: (chart: any) => {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea || !scales.y) return;

        const axis = chart.scales.y;
        if (!axis || !axis.ticks) return;

        ctx.save();
        ctx.font = '9px Inter-Reg';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        
        axis.ticks.forEach((tick: any) => {
          if (!tick || tick.y === undefined) return;
          
          const label = tick.label;
          if (typeof label === 'string' && label.trim() !== '') {
            const labelColor = getLabelColor(label.trim());
            if (labelColor !== 'black') {
              const y = tick.y;
              const x = chartArea.left - 10;
              
              const textMetrics = ctx.measureText(label);
              const textWidth = textMetrics.width;
              const textHeight = 16;
              const padding = 8;
              
              const rectX = x - textWidth - padding;
              const rectY = y - textHeight / 2;
              const rectWidth = textWidth + padding * 2;
              const rectHeight = textHeight;
              
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(rectX - 2, rectY - 1, rectWidth + 4, rectHeight + 2);
              
              ctx.fillStyle = labelColor + 'CC';
              ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
              
              ctx.fillStyle = '#FFFFFF';
              ctx.fillText(label, x, y);
            }
          }
        });

        ctx.restore();
      }
    };
  }, [chartData, rangeData, getRegionColor]);

  const latestValueLinePlugin: Plugin<'line'> = useMemo(() => {
    if (!chartData.length) {
      return {
        id: 'latestValueLine',
        beforeDraw: () => { }
      };
    }

    const sortedData = [...chartData].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });

    const latestItem = sortedData[sortedData.length - 1];
    let latestDate: string;
    try {
      const dateParts = latestItem.date.split('T')[0];
      latestDate = new Date(`${dateParts}T00:00:00Z`).toISOString();
    } catch {
      latestDate = latestItem.date;
    }

    return {
      id: 'latestValueLine',
      beforeDraw: (chart: any) => {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;

        const meta = chart.getDatasetMeta(0);
        const dataset = chart.data.datasets[0];
        
        let latestPoint = null;
        for (let i = meta.data.length - 1; i >= 0; i--) {
          const point = meta.data[i];
          const pointData = dataset.data[i];
          if (pointData && pointData.x === latestDate) {
            latestPoint = point;
            break;
          }
        }

        if (!latestPoint) return;

        const xPosition = latestPoint.x;

        ctx.save();
        ctx.strokeStyle = '#0C2012';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);

        ctx.beginPath();
        ctx.moveTo(xPosition, chartArea.top);
        ctx.lineTo(xPosition, chartArea.bottom);
        ctx.stroke();

        ctx.restore();
      }
    };
  }, [chartData]);

  const xAxisTicks = useMemo(() => {
    if (!chartData.length) return { timestamps: [], dateMap: new Map<number, string>() };
    
    const sortedData = [...chartData].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });

    const dateMap = new Map<number, string>();
    const timestamps = sortedData.map((item) => {
      try {
        const date = new Date(item.date);
        if (!isNaN(date.getTime())) {
          const timestamp = date.getTime();
          dateMap.set(timestamp, item.date);
          return timestamp;
        }
      } catch {
        // Ignore invalid dates
      }
      return null;
    }).filter((v): v is number => v !== null);

    return { timestamps, dateMap };
  }, [chartData]);

  const xAxisBounds = useMemo(() => {
    if (xAxisTicks.timestamps.length === 0) return { min: undefined, max: undefined };
    
    const minDate = xAxisTicks.timestamps[0];
    const maxDate = xAxisTicks.timestamps[xAxisTicks.timestamps.length - 1];
    const timeSpan = maxDate - minDate;
    const padding = Math.max(timeSpan * 0.15, 30 * 24 * 60 * 60 * 1000);
    
    return {
      min: minDate - padding,
      max: maxDate + padding
    };
  }, [xAxisTicks]);

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 20,
          right: 20,
          top: 10,
          bottom: 40
        }
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'month',
            displayFormats: {
              month: 'MMM yyyy'
            }
          },
          min: xAxisBounds.min,
          max: xAxisBounds.max,
          offset: true,
          afterBuildTicks: (axis) => {
            if (xAxisTicks.timestamps.length > 0) {
              const monthYearMap = new Map<string, number>();
              xAxisTicks.timestamps.forEach((timestamp) => {
                const date = new Date(timestamp);
                const monthYear = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
                if (!monthYearMap.has(monthYear)) {
                  monthYearMap.set(monthYear, timestamp);
                }
              });
              
              let uniqueTicks = Array.from(monthYearMap.values()).sort((a, b) => a - b);
              
              if (uniqueTicks.length > 3) {
                const filteredTicks: number[] = [];
                const minTimeDiff = 45 * 24 * 60 * 60 * 1000; // 45 days
                
                uniqueTicks.forEach((tick, index) => {
                  if (index === 0 || index === uniqueTicks.length - 1) {
                    filteredTicks.push(tick);
                  } else {
                    const lastTick = filteredTicks[filteredTicks.length - 1];
                    if (tick - lastTick >= minTimeDiff) {
                      filteredTicks.push(tick);
                    } else {
                      const totalSpan = uniqueTicks[uniqueTicks.length - 1] - uniqueTicks[0];
                      const lastPosition = (lastTick - uniqueTicks[0]) / totalSpan;
                      const currentPosition = (tick - uniqueTicks[0]) / totalSpan;
                      
                      if (Math.abs(currentPosition - 0.5) < Math.abs(lastPosition - 0.5)) {
                        filteredTicks[filteredTicks.length - 1] = tick;
                      }
                    }
                  }
                });
                
                uniqueTicks = filteredTicks;
              }
              
              axis.ticks = uniqueTicks.map((tickValue) => ({
                value: tickValue
              }));
            }
          },
          ticks: {
            padding: 30,
            font: { size: 9, weight: 400 },
            color: 'black',
            maxRotation: 0,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 6,
            minSpacing: 100,
            callback: function (value: any) {
              try {
                let timestamp: number;
                if (typeof value === 'number') {
                  timestamp = value;
                } else if (typeof value === 'string') {
                  timestamp = new Date(value).getTime();
                } else if (value && typeof value === 'object' && value instanceof Date) {
                  timestamp = value.getTime();
                } else {
                  return '';
                }
                
                if (isNaN(timestamp)) {
                  return '';
                }
                
                const originalDateStr = xAxisTicks.dateMap.get(timestamp);
                if (originalDateStr) {
                  const date = new Date(originalDateStr);
                  const month = date.getUTCMonth();
                  const year = date.getUTCFullYear();
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `${monthNames[month]} ${year}`;
                }
                
                const dateValue = new Date(timestamp);
                if (isNaN(dateValue.getTime())) {
                  return '';
                }
                return format(dateValue, 'MMM yyyy');
              } catch {
                return '';
              }
            }
          },
          grid: {
            display: false
          }
        },
        y: {
          type: 'linear',
          position: 'left',
          min: (() => {
            if (isUnitsMode) {
              return yAxisTicksConfig.min;
            }

            const minValue = yLabels[0]?.value as number;
            const rangeStartsAtZero =
              rangeData?.critical_low_from === 0 ||
              rangeData?.critical_low_to === 0 ||
              rangeData?.low_from === 0 ||
              rangeData?.low_to === 0;
            if (rangeStartsAtZero && (minValue === undefined || minValue > 0)) {
              return 0;
            }
            return minValue;
          })(),
          max: yLabels[yLabels.length - 1]?.value as number,
          afterBuildTicks: (axis) => {
            axis.ticks = yAxisTicksConfig.evenlySpacedTicks.map((tickValue) => ({
              value: tickValue
            }));
          },
          ticks: {
            callback: (raw) => {
              const rawValue = typeof raw === 'number' ? raw : Number(raw);
              if (isNaN(rawValue)) return '';
              
              if (yAxisTicksConfig.evenlySpacedTicks.length === 0) return '';
              
              let closestTick = yAxisTicksConfig.evenlySpacedTicks[0];
              let minDiff = Math.abs(rawValue - closestTick);
              
              for (const tick of yAxisTicksConfig.evenlySpacedTicks) {
                const diff = Math.abs(rawValue - tick);
                if (diff < minDiff) {
                  minDiff = diff;
                  closestTick = tick;
                }
              }
              
              if (minDiff < 0.0001) {
                return yAxisTicksConfig.tickToLabelMap.get(closestTick) ?? '';
              }
              
              return '';
            },
            padding: 10,
            font: { size: 9, weight: 400 },
            color: 'black'
          },
          grid: {
            drawBorder: false,
            drawOnChartArea: false,
            color: 'transparent',
            borderDash: [5, 5],
            borderDashOffset: 0,
            lineWidth: 1
          },
          border: {
            display: true,
            color: '#b2b2b2',
            width: 1
          }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          mode: 'nearest',
          intersect: false,
          displayColors: false,
          backgroundColor: '#000000',
          titleColor: '#FFFFFF',
          bodyColor: '#FFFFFF',
          borderColor: '#000000',
          borderWidth: 1,
          padding: 12,
          callbacks: {
            title: (items) => {
              if (items.length === 0) return '';
              const dataIndex = items[0].dataIndex;
              if (dataIndex < 0 || dataIndex >= chartData.length) return '';
              const dateParts = chartData[dataIndex].date.split('T')[0];
              const date = new Date(`${dateParts}T00:00:00Z`);
              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            },
            label: (context) => {
              const dataPoint = context.raw as { originalValue?: number };
              const originalValue = dataPoint?.originalValue ?? context.parsed.y;
              const unitText = unit ? ` ${unit}` : '';
              return `Biomarker Value: ${originalValue}${unitText}`;
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        intersect: false
      }
    }),
    [yAxisTicksConfig, chartData, yLabels, xAxisTicks, xAxisBounds, isUnitsMode, unit, rangeData]
  );

  const chartPlugins = useMemo(
    () =>
      isUnitsMode
        ? [regionSeparatorPlugin, latestValueLinePlugin]
        : [rangeHighlightPlugin, regionSeparatorPlugin, latestValueLinePlugin, yAxisLabelColorPlugin],
    [isUnitsMode, rangeHighlightPlugin, regionSeparatorPlugin, latestValueLinePlugin, yAxisLabelColorPlugin]
  );

  if (loading) return <Loader />;
  if (error) return <div>Error loading chart data</div>;
  if (!chartData.length) return <div>No data</div>;

  return (
    <div style={{ height: '500px' }}>
      <Line data={data} options={options} plugins={chartPlugins} />
    </div>
  );
};

export default BiomarkerHistoryChart;
